<script setup lang="ts">
import { computed } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { groupsApi, type Group, type GroupForm } from '@/api/groups';
import { patientsApi } from '@/api/patients';
import { caregiversApi } from '@/api/caregivers';
import { usersApi } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import { useUser } from '@/composables/useUser';

interface Props {
  group?: Group | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { hasPermission } = usePermissions();

const queryClient = useQueryClient();

const isEditing = !!props.group;
const title = computed(() => (isEditing ? 'Editar Grupo' : 'Nuevo Grupo'));

const canManageMembers = hasPermission.value('groups', 'manage');
const canManageUsers = hasPermission.value('groups', 'manage');
// Coverage alerts follow care planning; with groups on, only Alerted Groups raise them.
const { user } = useUser();
const canToggleCoverageAlerts = computed(() => !!user?.value?.companyCarePlanningEnabled);

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 100;

const validationSchema = yup.object({
  name: yup.string()
    .required('El nombre es requerido')
    .min(MIN_NAME_LENGTH, `El nombre debe tener al menos ${MIN_NAME_LENGTH} caracteres`)
    .max(MAX_NAME_LENGTH, `El nombre no puede exceder ${MAX_NAME_LENGTH} caracteres`),
});

const { values, errors, defineField, handleSubmit, meta } = useForm<GroupForm>({
  validationSchema,
  initialValues: {
    name: props.group?.name || '',
    patientIds: props.group?.patientIds || [],
    caregiverIds: props.group?.caregiverIds || [],
    userIds: props.group?.userIds || [],
    coverageAlertsEnabled: props.group?.coverageAlertsEnabled ?? false,
  },
});

const [name, nameAttrs] = defineField('name');
const [patientIds] = defineField('patientIds');
const [caregiverIds] = defineField('caregiverIds');
const [userIds] = defineField('userIds');
const [coverageAlertsEnabled] = defineField('coverageAlertsEnabled');

const { data: patients } = useQuery({
  queryKey: ['patients', 'forFilters'],
  queryFn: () => patientsApi.indexForFilters(),
  initialData: [],
  enabled: canManageMembers,
});

const { data: caregivers } = useQuery({
  queryKey: ['caregivers', 'forFilters'],
  queryFn: () => caregiversApi.indexForFilters(),
  initialData: [],
  enabled: canManageMembers,
});

const { data: users } = useQuery({
  queryKey: ['users'],
  queryFn: () => usersApi.index(),
  initialData: [],
  enabled: canManageUsers,
});

const patientItems = computed(() =>
  patients.value.map(p => ({ value: p.id, title: p.fullName })),
);

const caregiverItems = computed(() =>
  caregivers.value.map(c => ({ value: c.id, title: c.fullName })),
);

const userItems = computed(() =>
  users.value.map(u => ({ value: u.id!, title: u.email })),
);

// Left out when the switch is hidden, so the server keeps the current value.
function coverageAlertsPayload(): Pick<GroupForm, 'coverageAlertsEnabled'> {
  return canToggleCoverageAlerts.value ? { coverageAlertsEnabled: !!values.coverageAlertsEnabled } : {};
}

function buildPayload(): GroupForm {
  const payload: GroupForm = { name: values.name, ...coverageAlertsPayload() };
  if (canManageMembers) {
    payload.patientIds = values.patientIds || [];
    payload.caregiverIds = values.caregiverIds || [];
  }
  if (canManageUsers) {
    payload.userIds = values.userIds || [];
  }
  return payload;
}

const createMutation = useMutation({
  mutationFn: () => groupsApi.create(buildPayload()),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['groups'] });
    emit('success');
    emit('close');
  },
});

const updateMutation = useMutation({
  mutationFn: () => groupsApi.update({ ...buildPayload(), id: props.group?.id }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['groups'] });
    emit('success');
    emit('close');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

const onSubmit = handleSubmit(() => {
  if (isEditing && props.group) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}
</script>

<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-text-field
          v-model="name"
          label="Nombre del grupo"
          :error-messages="errors.name"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="nameAttrs"
        />

        <v-autocomplete
          v-if="canManageMembers"
          v-model="patientIds"
          :items="patientItems"
          label="Pacientes"
          multiple
          chips
          closable-chips
          variant="outlined"
          class="mb-4"
          :disabled="isLoading"
        />

        <v-autocomplete
          v-if="canManageMembers"
          v-model="caregiverIds"
          :items="caregiverItems"
          label="Profesionales"
          multiple
          chips
          closable-chips
          variant="outlined"
          class="mb-4"
          :disabled="isLoading"
        />

        <v-autocomplete
          v-if="canManageUsers"
          v-model="userIds"
          :items="userItems"
          label="Usuarios con acceso"
          multiple
          chips
          closable-chips
          variant="outlined"
          class="mb-4"
          :disabled="isLoading"
          hint="Los usuarios asignados sólo verán los pacientes y profesionales del grupo."
          persistent-hint
        />

        <v-switch
          v-if="canToggleCoverageAlerts"
          v-model="coverageAlertsEnabled"
          label="Alertas de cobertura"
          color="primary"
          :disabled="isLoading"
          hint="Avisa las visitas planificadas de los pacientes del grupo que están por comenzar sin turno."
          persistent-hint
        />
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ isEditing ? 'Actualizar' : 'Crear' }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="createMutation.isError.value || updateMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente. Recuerda que el nombre no puede repetirse.
    </v-alert>
  </v-card>
</template>
